import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, HelpCircle } from 'lucide-react';

/* ───── Types ───── */

type ConfirmOptions = {
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
};

type PromptOptions = {
  title?: string;
  description?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

type ConfirmFn = (opts: string | ConfirmOptions) => Promise<boolean>;
type PromptFn = (opts: string | PromptOptions) => Promise<string | null>;

type Ctx = { confirm: ConfirmFn; prompt: PromptFn };

const ConfirmCtx = createContext<Ctx | null>(null);

/* ───── Provider ───── */

type ConfirmState = ConfirmOptions & { kind: 'confirm'; resolve: (v: boolean) => void };
type PromptState = PromptOptions & { kind: 'prompt'; resolve: (v: string | null) => void };
type ModalState = ConfirmState | PromptState | null;

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState>(null);

  const confirm: ConfirmFn = useCallback((opts) => {
    const normalized: ConfirmOptions = typeof opts === 'string' ? { description: opts } : opts;
    return new Promise<boolean>((resolve) => {
      setState({ kind: 'confirm', ...normalized, resolve });
    });
  }, []);

  const prompt: PromptFn = useCallback((opts) => {
    const normalized: PromptOptions = typeof opts === 'string' ? { description: opts } : opts;
    return new Promise<string | null>((resolve) => {
      setState({ kind: 'prompt', ...normalized, resolve });
    });
  }, []);

  function close(result: boolean | string | null) {
    if (!state) return;
    if (state.kind === 'confirm') state.resolve(typeof result === 'boolean' ? result : false);
    else state.resolve(typeof result === 'string' ? result : null);
    setState(null);
  }

  return (
    <ConfirmCtx.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) close(state?.kind === 'confirm' ? false : null); }}>
        {state?.kind === 'confirm' && <ConfirmBody state={state} onResolve={close} />}
        {state?.kind === 'prompt' && <PromptBody state={state} onResolve={close} />}
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

/* ───── Body components ───── */

function ConfirmBody({ state, onResolve }: { state: ConfirmState; onResolve: (v: boolean) => void }) {
  const destructive = state.tone === 'destructive';
  const okBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { okBtnRef.current?.focus(); }, []);

  return (
    <DialogContent>
      <DialogHeader>
        <div className="flex items-start gap-3">
          <span
            className={[
              'h-10 w-10 shrink-0 rounded-full grid place-items-center ring-1 ring-inset',
              destructive ? 'bg-destructive/12 text-destructive ring-destructive/20' : 'bg-muted text-muted-foreground ring-border',
            ].join(' ')}
          >
            {destructive
              ? <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
              : <HelpCircle className="h-4 w-4" strokeWidth={1.75} />}
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <DialogTitle>{state.title ?? (destructive ? '정말 삭제할까요?' : '확인')}</DialogTitle>
            {state.description && (
              <DialogDescription className="mt-2">{state.description}</DialogDescription>
            )}
          </div>
        </div>
      </DialogHeader>
      <DialogFooter className="gap-2">
        <Button variant="outline" size="sm" onClick={() => onResolve(false)}>
          {state.cancelLabel ?? '취소'}
        </Button>
        <Button
          ref={okBtnRef}
          variant={destructive ? 'destructive' : 'default'}
          size="sm"
          onClick={() => onResolve(true)}
        >
          {state.confirmLabel ?? (destructive ? '삭제' : '확인')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function PromptBody({ state, onResolve }: { state: PromptState; onResolve: (v: string | null) => void }) {
  const [value, setValue] = useState(state.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{state.title ?? '입력'}</DialogTitle>
        {state.description && <DialogDescription className="mt-1">{state.description}</DialogDescription>}
      </DialogHeader>
      <form
        onSubmit={(e) => { e.preventDefault(); onResolve(value); }}
        className="space-y-3"
      >
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={state.placeholder}
        />
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => onResolve(null)}>
            {state.cancelLabel ?? '취소'}
          </Button>
          <Button type="submit" size="sm">
            {state.confirmLabel ?? '확인'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/* ───── Hooks ───── */

// eslint-disable-next-line react-refresh/only-export-components -- Provider 와 훅 공존이 컨텍스트 이디엄
export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx.confirm;
}

// eslint-disable-next-line react-refresh/only-export-components -- Provider 와 훅 공존이 컨텍스트 이디엄
export function usePrompt() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('usePrompt must be used inside ConfirmProvider');
  return ctx.prompt;
}
